import React from 'react';
import { motion } from 'motion/react';
import { FileText, CheckCircle2, ArrowRight, Zap, Shield, Lock, Activity, Users, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import MultiStepLeadForm from '@/src/components/MultiStepLeadForm';

export default function Compliance() {
  return (
    <div className="relative pt-32 pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left Side: Sales Copy */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
            >
              <FileText className="w-3 h-3" /> Regulatory Excellence
            </motion.div>

            <div className="space-y-6">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-7xl font-display font-bold tracking-tighter leading-[0.9]"
              >
                Simplify Your <br />
                <span className="text-accent glow-text">PCI DSS Compliance.</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
              >
                Compliance shouldn't be a burden. We provide the expertise and tools to help you achieve and maintain PCI DSS certification with minimal friction.
              </motion.p>
            </div>

            {/* Pain Points */}
            <div className="space-y-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">The Compliance Hurdles We Solve:</h3>
              <div className="grid gap-4">
                {[
                  "Complex and confusing regulatory requirements",
                  "Heavy fines and penalties for non-compliance",
                  "Lost customer trust and brand reputation",
                  "Time-consuming and manual audit processes"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3 text-red-400/80">
                    <Shield className="w-5 h-5 shrink-0" />
                    <span className="text-sm font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <BarChart3 className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Rapid Certification</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Our streamlined process helps you achieve compliance faster, saving you time and resources.</p>
              </div>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <Activity className="w-8 h-8 text-accent" />
                <h4 className="font-bold">Continuous Monitoring</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">Stay compliant year-round with our automated monitoring and reporting tools.</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 rounded-2xl bg-accent/5 border border-accent/20">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold">Expert Guidance</p>
                <p className="text-xs text-muted-foreground">Work with certified QSA partners and compliance experts.</p>
              </div>
            </div>
          </div>

          {/* Right Side: Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="sticky top-32"
          >
            <div className="glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 blur-[60px] rounded-full" />
              <div className="text-center mb-10 space-y-2 relative z-10">
                <h2 className="text-3xl font-display font-bold">Get <span className="text-accent">Compliant</span></h2>
                <p className="text-sm text-muted-foreground font-light italic">Secure payments, secured trust.</p>
              </div>
              <MultiStepLeadForm />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
